import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import DownloadIcon from 'icons/Download.icon';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import utils, { getHasElementsInArray } from 'utils';
import {
  ParsedSpreadsheetRFIRow,
  RfiCreateStep,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { DOCS_FIELD_NAME } from '../../utils';
import SpreadsheetDropzone from './components/SpreadsheetDropzone';
import ParsedDocumentsTable from './components/ParsedDocumentsTable';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { AxiosError } from 'axios';
import { getMissingColumnsErrorMessage } from 'axiosUtils';

import './StepImportSpreadsheet.styles.scss';

const SPREADSHEET_FILE_NAME = 'dataRoomRfiSpreadsheet';

type StepImportSpreadsheetProps = {
  className?: string;
} & RfiCreateStep;

const StepImportSpreadsheet: React.FC<StepImportSpreadsheetProps> = (props) => {
  const { className } = props;

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const {
    input: { value: rows = [], onChange: setRows },
  } = useField<ParsedSpreadsheetRFIRow[]>(DOCS_FIELD_NAME);

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const [downloadTemplate, isDownloadingTemplate] = useCallbackAsync(
    useCallback(async () => {
      const { data } = await dataRoomApi.rfi.getRfiSpreadsheetTemplate(
        dataRoomId,
      );

      utils.downloadUrl({ type: FILE_TYPES.DOCUMENT_XLSX, href: data });
    }, [dataRoomId]),
  );

  const [uploadSpreadsheet, isUploading] = useCallbackAsync(
    useCallback(
      async (file: File) => {
        try {
          const { data } = await dataRoomApi.rfi.uploadRfiSpreadsheet(
            dataRoomId,
            file,
            SPREADSHEET_FILE_NAME,
          );

          setRows(data);
        } catch (e) {
          const missingColumnsMessage = getMissingColumnsErrorMessage(
            e as AxiosError,
          );

          popServerError(e, missingColumnsMessage);
        }
      },
      [dataRoomId, setRows, popServerError],
    ),
  );

  const classes = classNames('step-import-spreadsheet', className);

  return (
    <div className={classes}>
      {getHasElementsInArray(rows) ? (
        <ParsedDocumentsTable rows={rows} onRowsChange={setRows} />
      ) : (
        <>
          <div className="step-import-spreadsheet__header">
            <p className="step-import-spreadsheet__description">
              {t('eachRowBecomesOneDocument')}
            </p>

            <Button
              type="button"
              variant="outline"
              icon={DownloadIcon}
              disabled={isDownloadingTemplate}
              onClick={downloadTemplate}
            >
              {t('downloadRfiSpreadsheetTemplate')}
            </Button>
          </div>

          <SpreadsheetDropzone
            onUpload={uploadSpreadsheet}
            isUploading={isUploading}
          />
        </>
      )}
    </div>
  );
};

export default StepImportSpreadsheet;
